import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Box } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useGetInfographicTemplateDetails } from '../hooks/useGetInfographicTemplateDetails'
import SydButton from '../../../../../commonDesign/Button'
import Checkbox from '../../../../../molecules/Checkbox'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import { useSaveInfographicTemplateMutation } from '../hooks/useSaveInfographicTemplateMutation'
import { ADMIN_ROUTES } from '../../../../../../constants'
import Loading from '../../../../../molecules/Loading'
import { AutocompleteClientSelect } from '../../components/AutocompleteClientSelect'
import { ClientPreviewContextSelect } from '../../components/ClientPreviewContextSelect'
import { PreviewDisplayProvider } from '../../components/PreviewDisplayContext'
import { InfographicTemplateDisplay } from './InfographicTemplateDisplay'

export const EditInfographicTemplateForm = ({
  defaultTemplateId,
  onSubmit: _onSubmit
}) => {
  const history = useHistory()
  const form = useForm({
    defaultValues: {
      infographicTemplateId: defaultTemplateId,
      content: {},
      name: ''
    }
  })

  if (!defaultTemplateId) {
    history.replace(ADMIN_ROUTES.INFOGRAPHIC_REPORTS_TEMPLATE)
  }

  const infographicTemplateId = form.getValues('infographicTemplateId')
  const templateIsGlobal = form.watch('global')

  const { data, isFetching, refetch } = useGetInfographicTemplateDetails({
    infographicTemplateId: infographicTemplateId,
    includes: { levelTypes: true, infographicBlocks: true }
  }, { enabled: !!infographicTemplateId && infographicTemplateId !== 'NEW' })

  useEffect(() => {
    if (infographicTemplateId && data) {
      form.reset(data)
    }
    if (infographicTemplateId === 'NEW') {
      form.setValue('content', {
        pages: [{ blocks: [] }]
      })
      form.setValue('name', '')
    }
  }, [data, form, infographicTemplateId])

  const saveMutation = useSaveInfographicTemplateMutation()
  const onSubmit = async (data) => {
    const normalizedContent = {
      ...data.content,
      pages: data.content?.pages.map(page => ({
        ...page,
        blocks: page.blocks.map(({ infographicBlock, isEmptyBlock, ...block }) => block)
      }))
    }
    const response = await saveMutation.mutateAsync({
      ...data,
      infographicTemplateId: data.infographicTemplateId === 'NEW' ? null : data.infographicTemplateId,
      content: normalizedContent
    })
    await refetch()
    const parsedBody = JSON.parse(response.body)
    if (parsedBody.infographicTemplateId !== infographicTemplateId) {
      window.location.push(`${ADMIN_ROUTES.INFOGRAPHIC_REPORTS_TEMPLATE}/${parsedBody.infographicTemplateId}`)
    }
  }

  return (
    <FormProvider {...form}>
      <form {...form} onSubmit={form.handleSubmit(onSubmit)}>
        <div className='flex justify-end'>
          <ClientPreviewContextSelect />
        </div>

        <Box display='flex' gridGap='8px' flexWrap='wrap'>
          {isFetching ? (
            <Box flex={1} alignItems='center' justifyContent='center'>
              <Loading />
            </Box>
          ) : (
            <>
              <Box flex='1 1' maxWidth='500px'>
                <Controller
                  control={form.control}
                  name='name'
                  rules={{ required: { value: true, message: 'Name required' } }}
                  render={({ field, fieldState }) => (
                    <SydLabel label='Template Name' error={hookFormErrorAdapter(form, fieldState)} required>
                      <SydInput
                        size='sm'
                        {...field}
                      />
                    </SydLabel>
                  )}
                />

                <Controller
                  control={form.control}
                  name='global'
                  render={({ field, fieldState }) => (
                    <Box display='flex' gridGap={4} mt={2}>
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e, checked) => field.onChange(checked)}
                      />
                      <Box onClick={() => field.onChange(!field.value)}>
                        <SydLabel
                          label='Is Global template'
                          description='Template will be available to all clients'
                        />
                      </Box>
                    </Box>
                  )}
                />

                {!templateIsGlobal && (
                  <Box mt={2}>
                    <Controller
                      control={form.control}
                      name='levelTypes'
                      render={({ field, fieldState }) => (
                        <SydLabel label='Limited to Clients' error={fieldState?.error?.message}>
                          <AutocompleteClientSelect field={field} multiple />
                        </SydLabel>
                      )}
                    />
                  </Box>
                )}

                <Box display='flex' justifyContent='space-between' mt={4}>
                  <SydButton
                    variant='secondary'
                    type='button'
                    onClick={() => history.replace(ADMIN_ROUTES.INFOGRAPHIC_REPORTS_TEMPLATE)}
                    disabled={saveMutation.isLoading}
                  >
                    Cancel
                  </SydButton>
                  <SydButton
                    variant='primary'
                    type='submit'
                    processing={saveMutation.isLoading}
                    disabled={saveMutation.isLoading}
                  >
                    Save
                  </SydButton>
                </Box>
              </Box>
              <div className='flex-1 relative'>
                <PreviewDisplayProvider editingLevel='page'>
                  <InfographicTemplateDisplay />
                </PreviewDisplayProvider>
              </div>
            </>
          )}
        </Box>
      </form>
    </FormProvider>
  )
}

EditInfographicTemplateForm.propTypes = {
  defaultTemplateId: PropTypes.number,
  onSubmit: PropTypes.func
}
